import { bool, number, string } from "prop-types";
import React, { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { useTranslation } from "@lib/i18n";

import CEESubsidyDiscountStripeSection from "./CEESubsidyDiscountStripeSection";
import CheckMarkIcon from "@components/shared/Icons/Checkmark";
import Icon from "@components/shared/Icons/Icon";
import ArrowRightIcon from "@components/shared/Icons/ArrowRight";
import Typography from "@components/shared/Typography";
import ScrappageDetailsModal from "../PriceDetail/ScrappageDetailsModal";
import DiscountDetailsModal from "../PriceDetail/DiscountDetailsModal";
import EcoBonusModal from "@components/PriceDetail/EcoBonusModal";

import { withTabNavigation } from "@hoc/withTabNavigation";

import {
  ecologicalBonusClickEvent,
  toggleScrappageModalEvent,
} from "@gtm/events/homepage";
import gtm from "@gtm/core";

import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { CEE_SUBSIDY_TYPES } from "@shared/constants";

const CLASSNAME = {
  TYPOGRAPHY:
    "inline-block text-left text-primaryDark text-xs tracking-wider uppercase font-extrabold text-balance",
  LINK: "cursor-pointer text-primaryOrange inline-flex items-center gap-1",
};

const LinkTypography = withTabNavigation(Typography);

const DiscountStripe = ({
  className,
  disabled,
  governmentDiscountPercentage,
  isFixed,
  isMovesProgramEnabled,
  isScrappageEnabled,
  isScrappageSelected,
  scrappageDiscountPercentage,
  withEcoBonus,
}) => {
  const { t, i18n } = useTranslation();
  const containerRef = useRef(null);
  const isB2BSelected = useIsB2BSelected();

  const [isScrappageModalOpen, setIsScrappageModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEcoBonusModalOpen, setIsEcoBonusModalOpen] = useState(false);

  const openDetailsModal = () => {
    gtm.fire(
      ecologicalBonusClickEvent({
        displayClose: "Display",
        label: t("price.openDetails"),
      })
    );
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    gtm.fire(
      ecologicalBonusClickEvent({
        displayClose: "Close",
        label: "close",
      })
    );

    setIsDetailsModalOpen(false);
  };

  const openScrappageModal = () => {
    gtm.fire(
      toggleScrappageModalEvent({
        displayClose: "Display",
        label: t(isScrappageSelected ? "price.deactivate" : "price.clickHere"),
      })
    );

    setIsScrappageModalOpen(true);
  };

  const closeScrappageModal = () => {
    gtm.fire(
      toggleScrappageModalEvent({
        displayClose: "Close",
        label: "close",
      })
    );

    setIsScrappageModalOpen(false);
  };

  const toggleEcologicalBonus = () => {
    gtm.fire(
      ecologicalBonusClickEvent({
        displayClose: isEcoBonusModalOpen ? "Close" : "Display",
        label: isEcoBonusModalOpen
          ? ""
          : isB2BSelected
          ? t("price.ecologicalBonusB2B")
          : t("price.ecologicalBonus"),
      })
    );

    setIsEcoBonusModalOpen(!isEcoBonusModalOpen);
  };

  return (
    <div className={twMerge("relative z-10", className)} ref={containerRef}>
      <div
        className={twMerge(
          "flex flex-col justify-between w-full text-black rounded-b-lg shadow-sm",
          isFixed ? "bg-white/90 backdrop-blur-md" : "bg-white"
        )}
      >
        {withEcoBonus && (
          <>
            {governmentDiscountPercentage !== 0 && (
              <div className="flex justify-start items-center gap-2">
                <CheckMarkIcon width="26px" height="26px" checked />
                <Typography className={CLASSNAME.TYPOGRAPHY}>
                  {governmentDiscountPercentage}%
                </Typography>
                <Typography
                  className={CLASSNAME.TYPOGRAPHY}
                  data-id="discount-ecobonus-text"
                >
                  {t("price.ecoBonus")}
                </Typography>
                <LinkTypography
                  className={twMerge(CLASSNAME.TYPOGRAPHY, CLASSNAME.LINK)}
                  onClick={openDetailsModal}
                >
                  {t("price.openDetails")}
                  <Icon className="size-3" icon={<ArrowRightIcon />} />
                </LinkTypography>
              </div>
            )}
            {(governmentDiscountPercentage === 0 || isMovesProgramEnabled) && (
              <div className="flex justify-start items-center gap-2">
                <CheckMarkIcon width="26px" height="26px" checked />
                <LinkTypography
                  className={twMerge(CLASSNAME.TYPOGRAPHY, CLASSNAME.LINK)}
                  data-id="discount-ecobonus-text"
                  onClick={toggleEcologicalBonus}
                >
                  {isB2BSelected
                    ? t("price.ecologicalBonusB2B")
                    : t("price.ecologicalBonus")}
                  <Icon className="size-3" icon={<ArrowRightIcon />} />
                </LinkTypography>
              </div>
            )}
          </>
        )}
        {!withEcoBonus && isMovesProgramEnabled && (
          <div className="flex justify-start items-center gap-2">
            <CheckMarkIcon width="26px" height="26px" checked />
            <LinkTypography
              className={twMerge(CLASSNAME.TYPOGRAPHY, CLASSNAME.LINK)}
              data-id="discount-ecobonus-text"
              onClick={toggleEcologicalBonus}
            >
              {isB2BSelected && i18n.exists("price.ecologicalBonusB2B")
                ? t("price.ecologicalBonusB2B")
                : t("price.ecologicalBonus")}
              <Icon className="size-3" icon={<ArrowRightIcon />} />
            </LinkTypography>
          </div>
        )}
        {isScrappageEnabled && (
          <div className="flex justify-start items-center gap-2">
            <CheckMarkIcon
              className={isScrappageSelected ? "text-black" : "text-greyLight"}
              checked
              height="26px"
              width="26px"
            />
            {isScrappageSelected && (
              <Typography className={CLASSNAME.TYPOGRAPHY}>
                {scrappageDiscountPercentage}%
              </Typography>
            )}
            <Typography className={CLASSNAME.TYPOGRAPHY}>
              {isScrappageSelected
                ? t("price.scrappBonusOn")
                : t("price.scrappBonusOff")}
            </Typography>
            {!disabled && (
              <LinkTypography
                className={twMerge(CLASSNAME.TYPOGRAPHY, CLASSNAME.LINK)}
                onClick={openScrappageModal}
              >
                {t(
                  isScrappageSelected ? "price.deactivate" : "price.clickHere"
                )}
                <Icon className="size-3" icon={<ArrowRightIcon />} />
              </LinkTypography>
            )}
          </div>
        )}
        <DiscountDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={closeDetailsModal}
        />
        {isScrappageEnabled && (
          <ScrappageDetailsModal
            isOpen={isScrappageModalOpen}
            isScrappageSelected={isScrappageSelected}
            onClose={() => setIsScrappageModalOpen(false)}
            closeScrappageModal={closeScrappageModal}
          />
        )}
        <CEESubsidyDiscountStripeSection
          disabled={disabled}
          ceeSubsidyType={CEE_SUBSIDY_TYPES.PERSONAL}
        />
        <CEESubsidyDiscountStripeSection
          disabled={disabled}
          ceeSubsidyType={CEE_SUBSIDY_TYPES.BUSINESS}
        />
        <CEESubsidyDiscountStripeSection
          disabled={disabled}
          ceeSubsidyType={CEE_SUBSIDY_TYPES.GOVERNMENT}
        />
        {(withEcoBonus || isMovesProgramEnabled) && (
          <EcoBonusModal
            isOpen={isEcoBonusModalOpen}
            onClose={toggleEcologicalBonus}
          />
        )}
      </div>
    </div>
  );
};

DiscountStripe.propTypes = {
  governmentDiscountPercentage: number.isRequired,
  scrappageDiscountPercentage: number.isRequired,
  className: string,
  disabled: bool,
  isFixed: bool,
  isMovesProgramEnabled: bool,
  isScrappageEnabled: bool,
  isScrappageSelected: bool,
  withEcoBonus: bool,
};

export default DiscountStripe;
